import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

type GeoKeywordsInputProps = {
  value: string[];
  onChange: (keywords: string[]) => void;
  maxKeywords?: number;
};

export function GeoKeywordsInput({ value, onChange, maxKeywords = 5 }: GeoKeywordsInputProps) {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword();
    }
  };

  const addKeyword = () => {
    const keyword = inputValue.trim().toLowerCase();
    if (keyword && !value.includes(keyword) && value.length < maxKeywords) {
      onChange([...value, keyword]);
      setInputValue("");
    }
  };

  const removeKeyword = (keyword: string) => {
    onChange(value.filter((k) => k !== keyword));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {value.map((keyword) => (
          <Badge
            key={keyword}
            variant="secondary"
            className="px-2 py-0.5 text-xs gap-1"
          >
            {keyword}
            <button
              type="button"
              onClick={() => removeKeyword(keyword)}
              className="ml-1 hover:text-destructive transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addKeyword}
        placeholder={value.length >= maxKeywords ? "Max keywords reached" : "Type keyword and press Enter"}
        disabled={value.length >= maxKeywords}
        className="bg-background/50 border-border/30"
      />
      <p className="text-xs text-muted-foreground">
        {value.length}/{maxKeywords} keywords · Helps AI search engines find your prompt
      </p>
    </div>
  );
}
